import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  Psychology,
  Assessment,
} from '@mui/icons-material';
import {
  PatientAssessment,
  AssessmentSummary,
  assessmentUtils,
} from '../../services/providerAssessmentService';

interface PatientAssessmentSummaryProps {
  summary: AssessmentSummary | null;
  assessments: PatientAssessment[];
  focusTemplate?: string; // Optional: focus on specific assessment type
}

const PatientAssessmentSummary: React.FC<PatientAssessmentSummaryProps> = ({
  summary,
  assessments,
  focusTemplate,
}) => {
  const phq9Assessment = assessmentUtils.getAssessmentByType(assessments, 'phq9');
  const gad7Assessment = assessmentUtils.getAssessmentByType(assessments, 'gad7');
  const biopsychosocialAssessment = assessmentUtils.getAssessmentByType(assessments, 'biopsychosocial');

  // If focusing on a specific template, show only that assessment
  if (focusTemplate) {
    const focusAssessment = assessmentUtils.getAssessmentByType(assessments, focusTemplate);
    if (!focusAssessment) {
      return (
        <Typography variant="body2" color="text.secondary">
          Assessment not found or not completed.
        </Typography>
      );
    }

    return (
      <Box>
        <AssessmentDetailView assessment={focusAssessment} />
      </Box>
    );
  }

  // Show overall summary
  return (
    <Box>
      {/* Overall Progress */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Assessment Completion Overview
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assessment Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(summary?.completedAssessments ?? 0) / (summary?.totalAssessments ?? 1) * 100}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {summary?.completedAssessments ?? 0} of {summary?.totalAssessments ?? 0} assessments completed
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign="center">
            <Typography variant="h3" color="primary">
              {Math.round(((summary?.completedAssessments ?? 0) / (summary?.totalAssessments ?? 1)) * 100)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Assessment Scores */}
      <Grid container spacing={3}>
        {/* PHQ-9 Score */}
        {phq9Assessment && (
          <Grid item xs={12} md={4}>
            <ScoreCard
              title="PHQ-9 Depression"
              score={phq9Assessment.totalScore}
              maxScore={27}
              level={phq9Assessment.scoreLevel}
              interpretation={phq9Assessment.scoreInterpretation}
              icon={<Psychology />}
              color="info"
            />
          </Grid>
        )}

        {/* GAD-7 Score */}
        {gad7Assessment && (
          <Grid item xs={12} md={4}>
            <ScoreCard
              title="GAD-7 Anxiety"
              score={gad7Assessment.totalScore}
              maxScore={21}
              level={gad7Assessment.scoreLevel}
              interpretation={gad7Assessment.scoreInterpretation}
              icon={<Assessment />}
              color="secondary"
            />
          </Grid>
        )}

        {/* FMLA Eligibility */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp 
                sx={{ 
                  fontSize: 40, 
                  color: assessmentUtils.calculateFMLAEligibility(assessments) ? 'success.main' : 'warning.main',
                  mb: 1 
                }} 
              />
              <Typography variant="h6" gutterBottom>
                FMLA Status
              </Typography>
              <Chip 
                label={assessmentUtils.calculateFMLAEligibility(assessments) ? 'Eligible' : 'Review Required'}
                color={assessmentUtils.calculateFMLAEligibility(assessments) ? 'success' : 'warning'}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {assessmentUtils.calculateFMLAEligibility(assessments) 
                  ? 'Patient meets clinical criteria for FMLA certification'
                  : 'Additional clinical evaluation may be required'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clinical Notes */}
      {summary?.clinicalNotes && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Clinical Notes
          </Typography>
          <Typography variant="body1">
            {summary.clinicalNotes}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Component for individual score cards
interface ScoreCardProps {
  title: string;
  score?: number;
  maxScore: number;
  level?: string;
  interpretation?: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  score,
  maxScore,
  level,
  interpretation,
  icon,
  color,
}) => (
  <Card>
    <CardContent sx={{ textAlign: 'center' }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>
        {icon}
      </Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h3" color={color} gutterBottom>
        {score ?? 'N/A'}
        <Typography variant="caption" color="text.secondary">
          /{maxScore}
        </Typography>
      </Typography>
      {level && (
        <Chip 
          label={assessmentUtils.formatScoreLevel(level)}
          color={assessmentUtils.getSeverityColor(level)}
          size="small"
          sx={{ mb: 1 }}
        />
      )}
      {interpretation && (
        <Typography variant="body2" color="text.secondary">
          {interpretation}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Detailed view for individual assessment
const AssessmentDetailView: React.FC<{ assessment: PatientAssessment }> = ({ assessment }) => {
  const isScored = assessment.totalScore !== undefined;

  return (
    <Box>
      {/* Assessment Header */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {getAssessmentDisplayName(assessment.templateId)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed: {assessment.completedAt ? new Date(assessment.completedAt).toLocaleString() : 'In Progress'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} textAlign="right">
            {isScored && (
              <Box>
                <Typography variant="h4" color="primary">
                  {assessment.totalScore}
                </Typography>
                {assessment.scoreLevel && (
                  <Chip 
                    label={assessmentUtils.formatScoreLevel(assessment.scoreLevel)}
                    color={assessmentUtils.getSeverityColor(assessment.scoreLevel)}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Score Interpretation */}
      {assessment.scoreInterpretation && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Clinical Interpretation
          </Typography>
          <Typography variant="body1">
            {assessment.scoreInterpretation}
          </Typography>
        </Paper>
      )}

      {/* Response Summary */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Response Summary
        </Typography>
        <Grid container spacing={1}>
          {Object.entries(assessment.responses).map(([questionId, response], index) => (
            <Grid item xs={12} key={questionId}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Q{index + 1}:
                </Typography>
                <Typography variant="body2">
                  {response}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

// Helper function to get display names for assessment types
const getAssessmentDisplayName = (templateId: string): string => {
  switch (templateId) {
    case 'phq9': return 'PHQ-9 Depression Scale';
    case 'gad7': return 'GAD-7 Anxiety Scale';
    case 'biopsychosocial': return 'Biopsychosocial Assessment';
    case 'document_upload': return 'Supporting Documents';
    default: return 'Assessment';
  }
};

export default PatientAssessmentSummary;